import React, { useContext, useEffect, useState } from "react";
import {
   Button, Card, CardBody, Col, Dropdown,
   DropdownItem, DropdownMenu, Input, Row, Table,
} from "reactstrap";
import classNames from "classnames";
import { toast } from "react-toastify";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import {
   ConfirmModal, CustomerTypeahead,
   FilterDateTime, ItemTypeahead,
   ServiceThreadSpinner, SortHeader,
} from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, filterHelpers, UserContext, } from "../utils";

const inactiveStyle = {
   color: "#2b72b9",
   backgroundColor: "lightGray",
   textTransform: "uppercase",
};

const today = new Date();
const oneYearAgo = dateHelpers.formatDateForServer((new Date(today)).setFullYear(today.getFullYear() - 1));

function CreateItemDropdownMenu({
   npiId,
   displayId,
   attachmentCount,
   isProductionItem,
}) {
   const navigate = useNavigate();
   const [open, setOpen] = useState(false);

   const reviewRoute = isProductionItem
      ? `${constants.ENDPOINTS.createProductionItem}/${npiId}`
      : `${constants.ENDPOINTS.createSampleItem}/${npiId}`;

   const attachmentRoute = isProductionItem
      ? `/attachments/${constants.ATTACHMENT_TYPES.NewItemRequest}/${displayId}`
      : `/attachments/${constants.ATTACHMENT_TYPES.SampleItemRequest}/${displayId}`;
   return (
      <td
         className="data-cell"
         onMouseEnter={() => setOpen(true)}
         onMouseLeave={() => setOpen(false)}
         style={{ position: "relative" }}
      >
         <span className="shredLink fw-bold noWrap">{displayId}</span>
         <Dropdown
            isOpen={open}
            toggle={() => setOpen(!open)}
            style={{ position: "absolute", top: "2.2em", left: "0.5em" }}
         >
            <DropdownMenu>
               <DropdownItem
                  children="Review"
                  onClick={() => navigate(reviewRoute)}
               />
               <DropdownItem onClick={() => navigate(attachmentRoute)}>
                  Attachments {attachmentCount > -1 ? `(${attachmentCount})` : null}
               </DropdownItem>
            </DropdownMenu>
         </Dropdown>
      </td>
   );
}

export default function CreateItemCode(props) {
   const navigate = useNavigate();
   const { ENDPOINTS } = constants;
   const { currentUser } = useContext(UserContext);
   const [confirmDeleteId, setConfirmDeleteId] = useState(null);
   const [customer, setCustomer] = useState(null);
   const [data, setData] = useState(null);
   const [filters, setFilters] = useState([
      { filterName: "StartDate", value: oneYearAgo },
      { filterName: "customerId", value: "" },
      { filterName: "itemNumber", value: "" },
      { filterName: "showActive", value: true },
   ]);
   const [isProductionItem, setIsProductionItem] = useState(true);
   const [sortDirection, setSortDirection] = useState("desc");
   const [sortField, setSortField] = useState("Id");
   const [working, setWorking] = useState(false);

   useEffect(() => {
      runQuery()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [sortField, sortDirection, isProductionItem, filters])

   function createPayload() {
      let payload = {
         SortField: sortField,
         SortDirection: sortDirection,
      };
      _.map(filters, (filter) => {
         payload[filter.filterName] = filter.value;
         if (filter.filterName === "itemNumber") {
            payload[filter.filterName] = filter.value.ddlValue;
         }
      });
      return payload;
   }

   function handleDelete(itemId) {
      if (_.isNil(confirmDeleteId)) return;
      setWorking(true);
      const url = isProductionItem
         ? "DeleteProductionItemRequest"
         : "DeleteSampleItemRequest";
      api
         .fetch(`Item/${url}/${confirmDeleteId}`)
         .then((response) => {
            if (response.data.success) {
               runQuery();
            } else {
               toast.error(response.data.message);
               setWorking(false);
            }
         })
         .catch((err) => {
            api.catchHandler(err);
            setWorking(false);
         })
         .finally(() => {
            setConfirmDeleteId(null);
         });
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
      setData(null);
   }

   function onSortChange(field, direction) {
      setSortField(field);
      setSortDirection(direction);
      setData(null);
   }

   function runQuery() {
      document.title = `Create Item Code`;
      setWorking(true);
      const url = isProductionItem
         ? "Item/GetProductionItemSearch"
         : "Item/GetSampleItemSearch";
      api
         .post(url, createPayload())
         .then((response) => {
            if (response.data.success) {
               setData(response.data.requests);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   }

   function toggleSampleActive(requestId) {
      setWorking(true);
      api
         .fetch(`Item/ToggleSampleItemActive/${requestId}`)
         .then((r) => {
            if (r.data.success) {
               runQuery();
            } else {
               toast.error(r.data.message);
               setWorking(false);
            }
         })
         .catch((err) => {
            api.catchHandler(err);
            setWorking(false);
         });
   }

   return (
      <DesktopContainer screenName="Search New Item Requests">
         <div className="filter-header">
            <Row className="pb-1">
               <Col xs={{ size: 2, offset: 1 }} />
               <Col xs={{ size: 2 }}>
                  {" "}
                  <Button
                     style={{ width: "100%" }}
                     title="New Production Item"
                     className="shredButton float-start"
                     onClick={() => navigate(ENDPOINTS.createProductionItem + "/0")}
                  >
                     Create New <br /> Production Item
                  </Button>
               </Col>
               <Col xs={{ size: 2 }} />
               <Col xs={{ size: 2 }}>
                  <Button
                     style={{ width: "100%" }}
                     title="NewSampleItem"
                     className="shredButton float-end ms-2"
                     onClick={() => navigate(ENDPOINTS.createSampleItem + "/0")}
                  >
                     Create New <br /> Sample Item
                  </Button>
               </Col>
            </Row>
            <Row>
               <Col xs={12}>
                  <Row className="my-2">
                     <Col xs={{ size: 2, offset: 1 }} className="mt-1">
                        <h5 children="Start Date:" className="float-end text-white" />
                     </Col>
                     <Col xs={2}>
                        <FilterDateTime
                           className="form-control"
                           filterName="StartDate"
                           value={dateHelpers.formatDateForServer(filterHelpers.getValue(filters, "StartDate"))}
                           onChangeCallback={(e) => onFilterChange(e)}
                        />
                     </Col>
                     <Col xs={2} className="mt-1">
                        <h5 children="End Date:" className="float-end text-white" />
                     </Col>
                     <Col xs={2}>
                        <FilterDateTime
                           className="form-control"
                           filterName="EndDate"
                           value={dateHelpers.formatDateForServer(filterHelpers.getValue(filters, "EndDate"))}
                           onChangeCallback={(e) => onFilterChange(e)}
                        />
                     </Col>
                  </Row>
                  <Row className="my-2">
                     <Col xs={{ size: 2, offset: 1 }} className="mt-1">
                        <h5 children="Item Number:" className="float-end text-white" />
                     </Col>
                     <Col xs={6}>
                        <ItemTypeahead
                           value={filterHelpers.getValue(filters, "itemNumber")}
                           isClearable
                           includeSampleItems
                           onChange={(e) => {
                              if (e) {
                                 onFilterChange({
                                    filterName: "itemNumber",
                                    value: e,
                                    filteredDisplay: `Item: ${e.label}`,
                                 });
                              } else {
                                 setFilters(_.reject(filters, (x) => x.filterName === "itemNumber"));
                              }
                           }}
                        />
                     </Col>
                  </Row>
                  {!isProductionItem && (
                     <Row className="my-2">
                        <Col xs={{ size: 2, offset: 1 }}>
                           <h5 children="Customer:" className="text-white float-end" />
                        </Col>
                        <Col xs={6}>
                           {!currentUser?.isAnyCustomerTypeRole && (
                              <CustomerTypeahead
                                 value={customer}
                                 onChange={(c) => {
                                    setCustomer(c);
                                    if (c) {
                                       onFilterChange({
                                          filterName: "customerId",
                                          value: c.value,
                                          filteredDisplay: `Customer: ${c.label}`,
                                       });
                                    } else {
                                       setFilters(_.reject(filters, (x) => x.filterName === "customerId"));
                                    }
                                 }}
                              />
                           )}
                        </Col>
                     </Row>
                  )}
                  <Row>
                     <Col xs={{ size: 2, offset: 3 }} className="text-white mt-2">
                        <Input
                           type="checkbox"
                           className="me-3"
                           checked={filterHelpers.getValue(filters, "showActive")}
                           onChange={(e) => {
                              if (e) {
                                 onFilterChange({
                                    filterName: "showActive",
                                    value: e.target.checked,
                                 });
                              } else {
                                 setFilters(_.reject(filters, (x) => x.filterName === "showActive"));
                              }
                           }}
                        />
                        <span className="fw-bold">Exclude Hidden Items</span>
                     </Col>
                     <Col xs={{ size: 5 }} style={{ marginLeft: "12%" }}>
                        <Button
                           title="NewProductionItem"
                           className={classNames({ shredButton: isProductionItem }, "ms-2")}
                           style={isProductionItem ? null : inactiveStyle}
                           onClick={() => setIsProductionItem(!isProductionItem)}
                        >
                           Production Item
                        </Button>
                        <Button
                           title="NewSampleItem"
                           className={classNames({ shredButton: !isProductionItem }, "ms-2")}
                           style={!isProductionItem ? null : inactiveStyle}
                           onClick={() => setIsProductionItem(!isProductionItem)}
                        >
                           Sample Item
                        </Button>
                     </Col>
                  </Row>
                  <Row>
                     <Col xs={{ size: 2, offset: 3 }} className="text-white mt-2">Rows returned will max at 500</Col>
                  </Row>
               </Col>
            </Row>
         </div>
         {data ? (
            <ServiceThreadSpinner on={working}>
               {data.length ? (
                  <Card className="dashboard-card">
                     <CardBody>
                        <Table className="data-table m-2">
                           <thead className="table-header">
                              <tr>
                                 <th>
                                    <SortHeader
                                       displayName="ID"
                                       field="Id"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "Id"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Requested"
                                       field="RequestDate"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "RequestDate"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Reference Item"
                                       field="ReferenceItem"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "ReferenceItem"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Item"
                                       field="ItemDescription"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "ItemDescription"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>Status</th>
                                 <th className={classNames({ "d-none": isProductionItem })} />
                                 <th />
                              </tr>
                           </thead>
                           <tbody>
                              {_.map(data, (datum, index) => {
                                 return (
                                    <tr key={index}>
                                       <CreateItemDropdownMenu
                                          isProductionItem={isProductionItem}
                                          npiId={datum.id}
                                          displayId={datum.displayId}
                                          attachmentCount={datum.attachmentCount}
                                       />
                                       <td
                                          className="data-cell"
                                          children={dateHelpers.monthDayAndYear(datum.requestDate)}
                                          style={{ textAlign: "center" }}
                                       />
                                       <td
                                          className="data-cell"
                                          children={datum.referenceItemString}
                                          style={{ textAlign: "center" }}
                                       />
                                       <td
                                          className="data-cell"
                                          children={datum.itemWithDescription}
                                          style={{ textAlign: "center" }}
                                       />
                                       <td
                                          className="data-cell"
                                          children={datum.status}
                                          style={{ textAlign: "center" }}
                                       />
                                       <td className={classNames({"d-none": isProductionItem,})} >
                                          <Button
                                             size="sm"
                                             className="shredLink fw-bold noWrap"
                                             onClick={() => toggleSampleActive(datum.id)}
                                          >
                                             {datum.isActive ? "Hide" : "Show"}
                                          </Button>
                                       </td>
                                       <td>
                                          <Button
                                             size="sm"
                                             className="shredLink fw-bold noWrap"
                                             onClick={() => { setConfirmDeleteId(datum.id); }}
                                          >
                                             Delete
                                          </Button>
                                       </td>
                                    </tr>
                                 );
                              })}
                           </tbody>
                        </Table>
                     </CardBody>
                  </Card>
               ) : (
                  <div className="h5 text-center mt-2">No data found</div>
               )}
            </ServiceThreadSpinner>
         ) : null}
         <ConfirmModal
            confirmDelete={confirmDeleteId}
            approveConfirmDelete={handleDelete}
            declineConfirmDelete={() => setConfirmDeleteId(null)}
            disabled={working}
            warningMessage={
               <div className="text-center">
                  Are you sure you want to delete this request?
                  <br />
                  <span className={classNames("fw-bold text-danger", { "d-none": !isProductionItem, })} >
                     NOTE: Deleting a request will only mark the item as discontinued
                     inside Great Plains.
                  </span>
               </div>
            }
         />
      </DesktopContainer>
   );
}
